import React from 'react'
import styled, { css } from 'styled-components'

import Section from '../Section'
import Header, { Title } from '../Header'
import Separator from '../Separator'
import {
  Flex,
  FlexProps,
  truncateStyles,
  TruncateWithTooltip,
} from '@sketch/components'
import { Image as ImageUnstyled } from '@sketch/modules-common'
import { SymbolLink, SymbolUnavailable } from '../../../Symbol'
import { Chevron } from '../TextOrLayerStyles'

import { getArtboardThumbnail } from 'modules/shares/utils/getArtboardThumbnail'

import {
  GetArtboardInDocumentWithUuidQuery,
  useGetDetailViewArtboardQuery,
} from '@sketch/gql-types'
import { useVersioning } from 'modules/versioning'

const SymbolDetail = styled(Flex)<FlexProps & SymbolBoxProps>(
  ({ theme }) => css`
    font-size: ${theme.fontSizes.D};
    color: ${theme.colors.foreground.secondary.C};
    border: 1px solid ${theme.colors.border.A};
    border-radius: 5px;
    ${truncateStyles};
  `
)

interface SymbolBoxProps extends FlexProps {
  isClickable?: boolean
}

const SymbolBox = styled(SymbolDetail)<SymbolBoxProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  cursor: ${props => (props.isClickable ? 'pointer' : 'auto')};
`

const SymbolContainer = styled(Flex)<FlexProps>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  color: ${({ theme }) => theme.colors.foreground.secondary.A};
  font-weight: 400;
  line-height: 1.2;
  ${truncateStyles};
`

const SymbolPath = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.foreground.secondary.D};
    font-size: ${theme.fontSizes.B};
  `
)

const SymbolThumbnail = styled(ImageUnstyled)`
  display: flex;
  justify-content: center;

  img {
    max-width: 32px;
    max-height: 32px;
  }
`

type SymbolArtboard = OmitSafe<
  NonNullable<GetArtboardInDocumentWithUuidQuery['artboardInDocumentWithUuid']>,
  '__typename'
>

interface SymbolProps {
  name: string
  symbolArtboard: SymbolArtboard | null
  shareIdentifier: string
  path?: string
  isForeign?: boolean
  foreignVersionShortId?: string
  permanentArtboardShortId?: string
}

const SymbolInSidebar: React.FC<SymbolProps> = ({
  name,
  symbolArtboard,
  shareIdentifier,
  path,
  isForeign,
  foreignVersionShortId,
  permanentArtboardShortId,
}) => {
  const { versionShortId } = useVersioning()

  const { data } = useGetDetailViewArtboardQuery({
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.permanentArtboardShortId !== curr?.permanentArtboardShortId,
    // To account for artboard updates we're setting a cache-and-network
    // policy for artboard queries without an explicit version.
    fetchPolicy: versionShortId ? 'cache-first' : 'cache-and-network',
    variables: {
      permanentArtboardShortId: symbolArtboard?.permanentArtboardShortId ?? '',
      documentVersionShortId: foreignVersionShortId ?? versionShortId,
      shareIdentifier,
    },
    skip:
      !versionShortId ||
      !shareIdentifier ||
      !symbolArtboard?.permanentArtboardShortId,
  })

  const symbolThumbnail = data?.artboard
    ? getArtboardThumbnail(data.artboard, 'S')
    : null

  const isSymbolText =
    !symbolArtboard?.isSymbolMaster ||
    !symbolArtboard?.permanentArtboardShortId ||
    // if we're at the last level of the symbol tree, we don't want to show the link to nowhere
    permanentArtboardShortId === symbolArtboard?.permanentArtboardShortId

  const symbolDetail = (
    <SymbolBox isClickable={!isSymbolText}>
      {symbolThumbnail && (
        <Flex width="32px" marginRight={2}>
          <SymbolThumbnail src={symbolThumbnail} alt="thumbnail" />
        </Flex>
      )}
      <SymbolContainer
        marginRight={2}
        data-testid="inspector-sidebar-symbol-info"
      >
        <TruncateWithTooltip>{name}</TruncateWithTooltip>
        <SymbolPath data-testid="inspector-sidebar-symbol-path">
          <TruncateWithTooltip>{path} </TruncateWithTooltip>
        </SymbolPath>
      </SymbolContainer>
      {!isSymbolText ? <Chevron /> : null}
    </SymbolBox>
  )

  return (
    <>
      <Separator />
      <Section data-testid="inspector-sidebar-symbol">
        <Header>
          <Title>Symbol</Title>
        </Header>
        {isSymbolText ? (
          <SymbolUnavailable>{symbolDetail}</SymbolUnavailable>
        ) : (
          <SymbolLink
            isForeign={!!isForeign}
            isSamePermanentArtboardShortId={
              permanentArtboardShortId ===
              symbolArtboard?.permanentArtboardShortId
            }
            symbolArtboard={symbolArtboard}
            shareIdentifier={shareIdentifier}
          >
            {symbolDetail}
          </SymbolLink>
        )}
      </Section>
    </>
  )
}

export { SymbolInSidebar }
